import { useCallback, useEffect, useMemo, useState } from 'react'
import { parseCookies } from 'nookies'
import debounce from 'lodash.debounce'

import { fetchPlacesAutocomplete } from 'features/places/services/placesAutocompleteApi'
import type {
  PlaceAutocompletePrediction,
  PlacesAutocompleteStatus,
} from 'features/places/types/placesAutocomplete'
import { goolgeMapGetPlaceDetail } from 'state/ducks/locations/apis'

export const usePlaceAutoCompletion = () => {
  const cookies = parseCookies()
  const [searchText, setSearchText] = useState<string>('')
  const [places, setPlaces] = useState<PlaceAutocompletePrediction[]>([])
  const [status, setStatus] = useState<PlacesAutocompleteStatus | null>(null)

  const searchPlaces = useCallback(
    async (text: string) => {
      if (!text) return

      try {
        const result = await fetchPlacesAutocomplete(text, cookies.jwt_token)
        setStatus(result.status)

        if (result.status === 'OK') {
          setPlaces(result.predictions)
        } else {
          setPlaces([])
        }
      } catch {
        setPlaces([])
        setStatus(null)
      }
    },
    [cookies.jwt_token]
  )

  const debouncedSearch = useMemo(() => {
    return debounce((text: string) => searchPlaces(text), 1000)
  }, [searchPlaces])

  const getGeocode = useCallback(
    async (placeId: string) => {
      const result = await goolgeMapGetPlaceDetail(placeId, cookies.jwt_token)

      if (result.status === 'OK') {
        const {
          result: { geometry },
        } = result
        return geometry.location
      }

      return null
    },
    [cookies.jwt_token]
  )

  useEffect(() => {
    if (searchText) {
      debouncedSearch(searchText)
    } else {
      debouncedSearch.cancel()
      setPlaces([])
      setStatus(null)
    }

    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch, searchText])

  return {
    places,
    status,
    searchText,
    setSearchText,
    setPlaces,
    getGeocode,
  }
}
